<template>
  <div class="saved-filter">
    <div class="d-flex flex-row gap-5">
      <el-input
        v-model="name"
        v-validate="'required'"
        name="name"
        :placeholder="$i18n.t('type_in_name_of_search')"
      ></el-input>
      <el-checkbox v-model="isPublic">{{ $i18n.t('public_saved_filter') }}</el-checkbox>
    </div>
    <el-alert
      v-if="errors.first('name')"
      title="error alert"
      type="error"
      :description="$i18n.t(errors.first('name'))"
      show-icon
    >
    </el-alert>
    <el-button type="primary" icon="el-icon-search" @click="saveSearch()" data-dismiss="modal">
      {{ $i18n.t('save_search') }}
    </el-button>
    <div v-if="globalItems.length" class="saved-filter-list">
      <div
        class="saved-filter-header d-flex flex-row justify-content-between align-items-center"
        :class="showGlobalItems ? '' : 'closed'"
      >
        <span>{{ $i18n.t('global_saved_filters') }}</span>
        <span class="material-icons" :class="showGlobalItems ? 'closed' : ''" @click="collapseList('global')"
          >expand_more</span
        >
      </div>
      <div v-if="showGlobalItems" class="saved-filter-item" v-for="(item, idx) in globalItems" :key="idx">
        <a @click="loadFilter(item)" data-dismiss="modal">{{ item.name }}</a>
        <span v-if="showDelete(item)" class="material-icons" @click="removeFilter(item.id)">delete</span>
      </div>
    </div>
    <div v-if="privateItems.length" class="saved-filter-list">
      <div
        class="saved-filter-header d-flex flex-row justify-content-between align-items-center"
        :class="showPrivateItems ? '' : 'closed'"
      >
        <span>{{ $i18n.t('private_saved_filters') }}</span>
        <span class="material-icons" :class="showPrivateItems ? 'closed' : ''" @click="collapseList('private')"
          >expand_more</span
        >
      </div>
      <div v-if="showPrivateItems" class="saved-filter-item" v-for="(item, idx) in privateItems" :key="idx">
        <a @click="loadFilter(item)" data-dismiss="modal">{{ item.name }}</a>
        <span v-if="showDelete(item)" class="material-icons" @click="removeFilter(item.id)">delete</span>
      </div>
    </div>
    <div class="saved-filter-list-empty" v-else>
      {{ $i18n.t('no_saved_searches') }}
    </div>
  </div>
</template>
<script>
import notify from '../../utils/mixin-notify'
import { cloneDeep, filter, get, isString, merge } from 'lodash'
import { createStorage } from '../../utils'
import { parseJSON } from '../../utils/json'

/**
 * @class SavedFilter
 *
 * @description saving filters for lists and stuff
 */
export default {
  name: 'SavedFilter',
  mixins: [notify],
  props: {
    showInList: {
      type: Array,
      default: () => [],
    },
    searchParams: {
      type: Object,
      default: () => ({}),
    },
    panelId: {
      type: String,
      default: '',
    },
    id: {
      type: String,
      default: '',
    },
    show: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      items: [],
      name: '',
      isPublic: false,
      showPrivateItems: true,
      showGlobalItems: true,
    }
  },
  computed: {
    binds() {
      return this.rawSqlNamespace ? get(this.$store, `getters[${this.rawSqlNamespace}/binds]`) : {}
    },
    conditions() {
      return this.rawSqlNamespace ? get(this.$store, `getters[${this.rawSqlNamespace}/conditions]`) : []
    },
    rawSqlNamespace() {
      return this.hasStore ? this.$get('rawSqlNamespace') : null
    },
    errors() {
      return this.veeValidationErrors || []
    },
    globalItems() {
      return filter(this.items, (item) => item.isPublic === true)
    },
    privateItems() {
      return filter(this.items, (item) => item.isPublic === false)
    },
    hasStore() {
      return get(this, '$storeNamespace', false)
    },
    mongoId() {
      return this.hasStore ? this.$get('mongoId') : ''
    },
    queryId() {
      return this.hasStore ? this.$get('queryId') : this.id
    },
    uniqueId() {
      return this.getPanelId ? get(this.getPanelId.match(/\d+$/), '0', 0) : this.mongoId
    },
    getSearchParams() {
      return this.hasStore ? this.$get('searchParams') : this.searchParams
    },
    getPanelId() {
      const _panelId = this.hasStore ? this.$get('panelId') : false
      return isString(_panelId) ? _panelId : this.panelId
    },
    listId() {
      return `list-${this.mongoId || this.queryId}`
    },
    getShowInList() {
      return this.hasStore ? this.$get('showInList') : this.showInList
    },
  },
  methods: {
    clearError() {
      this.name = ''
      this.$validator.reset()
    },
    collapseList(type) {
      if (type === 'global') {
        this.showGlobalItems = !this.showGlobalItems
      } else if (type === 'private') {
        this.showPrivateItems = !this.showPrivateItems
      }
    },
    createStorage() {
      const storageKey = this.$get('storageKey')
      this.localStore = createStorage(storageKey, { id: this.panelId })
    },
    loadFilter(filter) {
      if (!filter) return
      const params = get(filter, 'params', null)
      let showInList = get(filter, 'showInList', null)
      try {
        showInList = showInList || this.getShowInList
      } catch (e) {
        showInList = this.getShowInList
      }

      if (this.hasStore) {
        this.$dispatch('setShowInList', { showInList })
        this.localStore.setItemValue('showInList', showInList)
        this.setSearchParam(filter.id)
        this.setRawSqlBinds(filter)
      }

      this.$emit('load-list-filter', { params })
    },
    loadList() {
      return this.$client
        .execute({
          operationId: 'SavedFilter_find',
          parameters: {
            filter: JSON.stringify({
              where: {
                and: [
                  {
                    or: [{ createUser: `${window.CORE.params.ID_S_USER}` }, { isPublic: 'true' }],
                  },
                  { listId: this.listId },
                ],
              },
            }),
          },
        })
        .then(({ obj }) => {
          this.items = obj
        })
        .catch((e) => console.error(e))
    },
    removeFilter(id) {
      this.$client.apis['SavedFilter']
        ['SavedFilter_removeItem']({
          id,
        })
        .then(({ obj }) => {
          const noty = {
            title: 'filter_has_been_removed',
            text: '',
            type: 'info',
          }
          this.showNotification(noty)
          this.loadList()
        })
        .catch((e) => {
          const noty = {
            title: 'delete_error',
            text: 'please_try_again_later',
            type: 'error',
          }
          this.showNotification(noty)
        })
    },
    saveSearch() {
      this.$validator.validateAll().then((success) => {
        if (success) {
          const data = {
            list: this.list,
            name: this.name,
            params: this.getSearchParams,
            listId: this.listId,
            showInList: this.getShowInList,
            isPublic: this.isPublic,
            binds: this.binds,
            conditions: this.conditions,
          }

          this.$client.execute({
            operationId: 'SavedFilter_create',
            method: 'post',
            parameters: { data },
          }).then((response) => {
            if (get(response, 'status') === 200) {
              this.$emit('saved-filter-saved')
              this.clearError()
              const noty = {
                title: 'filter_saved_successfully',
                type: 'success',
              }
              this.showNotification(noty)
              if (this.hasStore) this.setSearchParam(get(response, 'obj.id'))
              this.loadList()
            } else {
              const noty = {
                title: 'save_error',
                text: 'please_try_again_later',
                type: 'error',
              }
              this.showNotification(noty)
            }
          })
        }
      })
    },
    setRawSqlBinds({ binds, conditions }) {
      if (this.rawSqlNamespace && conditions && binds) {
        this.$dispatch(`setBinds`, { binds: this.updateBindValues(binds) }, this.rawSqlNamespace)
        this.$dispatch(`setConditions`, { conditions: conditions }, this.rawSqlNamespace)
      }
    },
    setSearchParam(filterId) {
      const searchParams = new URLSearchParams(window.location.search)
      searchParams.set(`filter-${this.uniqueId}`, filterId)
      const newUrl = `${window.location.protocol}//${window.location.host}${
        window.location.pathname
      }?${searchParams.toString()}`
      window.history.pushState({ path: newUrl }, '', newUrl)
    },
    showDelete(item) {
      return item.createUser === `${get(window.CORE, 'params.ID_S_USER')}`
    },
    updateBindValues(binds) {
      const current = cloneDeep(this.binds)
      const newValues = {}
      for (const [bindName, bind] of Object.entries(parseJSON(binds, this.id))) {
        newValues[bindName] = { value: bind.value }
      }
      return merge(current, newValues)
    },
  },
  mounted() {
    this.loadList()
  },
  created() {
    if (this.hasStore) this.createStorage()
  },
}
</script>
<style lang="scss" scoped>
@import 'src/assets/sass/variables';
.saved-filter {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-bottom: 20px;
  h3 {
    color: $color-text-primary;
  }
}
.saved-filter-list {
  background: $light1;
  border-radius: 8px;
  padding: 15px;
}
.saved-filter-list-empty {
  font-size: 14px;
}
.saved-filter-item {
  background: $white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid $border-color-light;
  padding: 10px;
  font-size: 14px;
  color: $color-text-primary;
  line-height: 24px;
  transition: 0.5s all;
  a {
    cursor: pointer;
    padding-left: 5px;
  }
  span {
    cursor: pointer;
  }
  &:nth-child(2) {
    border-radius: 8px 8px 0px 0px;
  }
  &:last-child {
    border-radius: 0px 0px 8px 8px;
  }
  &:only-child {
    border-radius: 8px 8px 8px 8px;
  }
  &:hover {
    background: $light1;
  }
}
.saved-filter-header {
  padding-bottom: 10px;
  .material-icons {
    transition: all 0.5s;
    &.closed {
      transform: rotate(180deg);
    }
    &:hover {
      cursor: pointer;
    }
  }
  &.closed {
    padding-bottom: 0px;
  }
}
</style>
